import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Page from './Components/Page'
import { processDataService } from '../../services/processDataService'
import { useAuthContext } from '../../hooks/useAuthContext'
import { activityTemplateService } from '../../services/activityTemplateService'
import { pageTemplateService } from '../../services/pageTemplateService'
import {
  useAllValues,
  useCommentValues,
  useFieldCurrencyMap,
  useFieldValidationError,
  usePageDetails,
  usePreviousProcessData,
  useProcessContext,
  useReviewValues,
  useSelectedTableData,
  useTableSequenceAndHeaders,
} from '../../store'
import {
  ACTIVITY_TYPES,
  CURRENCY_COUNTRY_CODE,
  FIELD_TYPES,
  REDIRECT_TIMER,
  REVIEW_CONTEXTS,
  ROUTE_TYPES,
  STYLES,
  TOAST_SETTINGS,
  TOAST_SETTINGS_ERROR,
} from '../../utility/constants'
import makeStyles from '@mui/styles/makeStyles'
import { processTemplateService } from '../../services/processTemplateService'
import { fieldTemplateService } from '../../services/fieldTemplateService'
import { dependencyMapService } from '../../services/dependencyMapService'
import { listDataService } from '../../services/listDataService'
import DataEntryFormFooter from '../../components/DataEntry/DataEntryFormFooter'
import { toast } from 'react-toastify'
import { finalConstraintsValidation, requiredFieldValidation } from './utils/fieldUtil'
import ConfirmationBox from '../../components/Dashboard/ConfirmationBox'
import { queryService } from '../../services/queryService'
import DataEntryPageNav from './Components/DataEntryPageNav'
import { CircularProgress, Dialog, Typography } from '@mui/material'
import { getDataAcrossProcess, getFieldIdFromKey, getPageId } from './utils/utils'
import { setDataInCache } from './utils/cacheUtils'
import { userConfig } from '../../services/userConfigServices'
import { executeOutboundServerAction } from './utils/outboundAction'
import Loader from '../../components/core-ui/Loader'
import { currencyFieldService } from '../../services/currencyFieldService'
import { computeCurrency } from './utils/currency'
import DataPreview from '../../components/DataPreview/DataPreview'
import { DocumentService } from '../../services/documentsServices'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import { computeFieldSumAcrossProcessOnInitialLoad } from './utils/autoCompute'
import useIdleRedirect from '../../hooks/useIdleRedirect'

const useStyles = makeStyles(STYLES.COMMON)
const confirmationText = 'All values are accepted. Are you sure you want to send back?'

const DataEntryPage = () => {
  useIdleRedirect('/tasks')
  const { dataEntryPageTitle, dataEntrysubtitle } = useResponsiveTypography()

  const classes = useStyles()
  const navigate = useNavigate()

  const { user } = useAuthContext()
  const { process_id: processId, activity_template_id: activityTemplateId } = useParams()

  const [processData, setProcessData] = useState(null)
  const [processTemplate, setProcessTemplate] = useState(null)
  const [currentPageIndex, setCurrentPageIndex] = useState(null)
  const [pages, setPages] = useState([])
  const [activityTemplate, setActivityTemplate] = useState(null)
  const [isSubmissionDialogOpen, setSubmissionDialogOpen] = useState(false)
  const [allToRoutes, setAllToRoutes] = useState([])
  const [selectedToRoute, setSelectedToRoute] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [tableConstraints, setTableConstraints] = useState([])
  const [fieldConstraints, setFieldConstraints] = useState([])
  const [allSubfields, setAllSubfields] = useState([])
  const [listDataMetadata, setListDataMetadata] = useState([])
  const [showGoBackWarningDialog, setShowGoBackWarningDialog] = useState(false)
  const [fieldConstraintHiddenRoutes, setFieldConstraintHiddenRoutes] = useState([])
  const [contentLoaded, setContentLoaded] = useState(false)

  const setPageDetails = usePageDetails((state) => state.setPageDetails)

  const allValues = useAllValues((state) => state.allValues)
  const setInitialAllValues = useAllValues((state) => state.setInitialAllValues)
  const reviewValues = useReviewValues((state) => state.reviewValues)
  const setReviewValues = useReviewValues((state) => state.setReviewValues)
  const commentValues = useCommentValues((state) => state.commentValues)
  const setCommentValues = useCommentValues((state) => state.setCommentValues)
  const setSelectedData = useSelectedTableData((state) => state.setSelectedData)
  const selectableTableData = useSelectedTableData((state) => state.selectedTableData)
  const setPreviousProcessDataValues = usePreviousProcessData(
    (state) => state.setPreviousProcessData
  )

  const setInitialFieldCurrencyMap = useFieldCurrencyMap(
    (state) => state.setInitialFieldCurrencyMap
  )
  const fieldCurrencyMap = useFieldCurrencyMap((state) => state.fieldCurrencyMap)

  /* process context */
  const processContext = useProcessContext((state) => state.processContext)
  const setProcessContext = useProcessContext((state) => state.setProcessContext)

  /* Table Sequence and Headers */
  const tableSequenceAndHeaders = useTableSequenceAndHeaders(
    (state) => state.tableSequenceAndHeaders
  )

  const setTableSequenceAndHeaders = useTableSequenceAndHeaders(
    (state) => state.setTableSequenceAndHeaders
  )

  /* disabled field to skip validation */
  const setFieldValidationError = useFieldValidationError((state) => state.setFieldValidationError)

  const [isPageReady, setIsPageReady] = useState(false)
  const { headingStyles, paragraphStyles } = useResponsiveTypography()
  const getAllActivityData = async (user, activityTemplateId) => {
    try {
      const allPromises = [
        dependencyMapService.getListDataDep(user, activityTemplateId),
        activityTemplateService.getTemplate(user, activityTemplateId),
        fieldTemplateService.getConditionalEnableDisable(user, activityTemplateId),
        fieldTemplateService.getFieldConstraints(user, activityTemplateId),
        activityTemplateService.getAllToRoutes(user, activityTemplateId),
        fieldTemplateService.getTableConstraints(user, activityTemplateId),
        fieldTemplateService.getHighLightConstraints(user, activityTemplateId),
        fieldTemplateService.getFieldConstraintHiddenRoute(user),
      ]
      const activityRelatedData = await Promise.all(allPromises)
      const allData = {
        listDataDep: activityRelatedData[0].data,
        activityTemplate: activityRelatedData[1].data[0],
        conditionalEnableDisable: activityRelatedData[2].data,
        fieldConstraints: activityRelatedData[3].data,
        allToRoutes: activityRelatedData[4].data,
        tableConstraints: activityRelatedData[5].data,
        fieldHighlights: activityRelatedData[6].data,
        fieldConstraintHiddenRoutes: activityRelatedData[7].data,
      }

      return allData
    } catch (error) {
      console.error('getAllActivityData', error.message)
    }
  }

  useEffect(() => {
    return () => {
      setFieldValidationError({})
    }
  }, [])

  useEffect(() => {
    if (allToRoutes != null) {
      setSelectedToRoute(allToRoutes[0]?.id)
    }
  }, [allToRoutes])

  // get all process data
  useEffect(() => {
    if (!user?.token) {
      return
    }

    ;(async () => {
      try {
        const allActivityData = await getAllActivityData(user, activityTemplateId)
        setAllToRoutes(allActivityData.allToRoutes)
        setActivityTemplate(allActivityData.activityTemplate)
        setTableConstraints(allActivityData.tableConstraints)
        // setFieldHighlights(allActivityData.fieldHighlights)
        setFieldConstraints(allActivityData.fieldConstraints)
        // setListDataMetadata(allActivityData.listDataMetadata)
        setFieldConstraintHiddenRoutes(allActivityData.fieldConstraintHiddenRoutes)

        /* get process data */
        const processData = await processDataService.getData(user, processId)
        setProcessData(processData.data[0])

        // try {
        //   const previousAllReviews = await processDataService.getAllPreviousReviews(user, processId)
        //   setPreviousAllReviews(previousAllReviews.data)
        // } catch (error) {
        //   setPreviousAllReviews({})
        //   console.log('allReviewValue', error)
        // }

        /* get process template */
        const processTemplate = await processTemplateService.getProcessTemplate(
          user,
          processData.data[0]?.process_template_id
        )
        setProcessTemplate(processTemplate.data[0])

        /* get all pages for activity */
        const dataEntryActivityId =
          allActivityData.activityTemplate?.activity_type == ACTIVITY_TYPES.REVIEW
            ? allActivityData.activityTemplate?.associated_data_entry_activity_template_id
            : activityTemplateId

        const tableSubfieldsMap = await fieldTemplateService.getTableSubfieldsMapInActivity(
          user,
          dataEntryActivityId
        )

        const pageTemplates = await pageTemplateService.getPagesForActivity(
          user,
          dataEntryActivityId
        )
        setPages(pageTemplates.data)

        const allSubfieldsResult = await fieldTemplateService.getAllFieldsInActivity(
          user,
          dataEntryActivityId
        )
        setAllSubfields(allSubfieldsResult.data)

        const tableSubfields = await fieldTemplateService.getTableSubfields(
          user,
          dataEntryActivityId
        )

        const listDataMetadata =
          (await listDataService.getListDataMetadata(user, dataEntryActivityId))?.data || []
        setListDataMetadata(listDataMetadata)

        /* get field_dep - TODO: cache */
        const fieldTemplateDep = (
          await dependencyMapService.getFieldTemplateDep(user, dataEntryActivityId)
        ).data

        const previousProcessData = await processDataService.getPreviousProcessData(user, processId)
        setPreviousProcessDataValues(previousProcessData.data)

        const listDataForActivity = await listDataService.getDataActivity(user, activityTemplateId)
        Object.entries(listDataForActivity).forEach(([key, value]) => {
          setDataInCache(key, value)
        })

        const { computedSumAcrossProcess, ndc } = (
          await processDataService.getSumAcrossProcessComputation(user, processData.data[0]?.id)
        ).data

        const processAllRoutes = await activityTemplateService.getProcessAllRoutes(
          user,
          processData.data[0]?.process_template_id
        )

        if (!currentPageIndex) {
          const processId = processData?.data[0]?.id
          if (processId) {
            const defaultNavigatePage = await pageTemplateService.getDefaultPage(user, processId)
            currentPageIndex == null &&
              setCurrentPageIndex(() => defaultNavigatePage?.default_page_sequence - 1 || 0)
          }
        }

        const userTeamMap = await userConfig.getUserTeamMap(user)

        const nonDefaultCurrency = await currencyFieldService.getNonDefaultCurrency(processId, user)

        // set initial fieldCurrencyMap
        const fieldCurrencyMapData = nonDefaultCurrency?.reduce((acc, cur) => {
          return { ...acc, [cur.field_template_id]: CURRENCY_COUNTRY_CODE[cur.currency_code] }
        }, {})

        const companyDetails = await currencyFieldService.getCompanyData(user)

        const conditionalEnableDisable = (
          await fieldTemplateService.getConditionalEnableDisable(user, dataEntryActivityId)
        ).data

        /* get field hidden fields - TODO: cache */
        const hiddenFields = (
          await fieldTemplateService.getHiddenFieldsForUser(user, dataEntryActivityId)
        ).data

        /* get tables sequence and headers*/
        const tableSequenceAndHeaders = await fieldTemplateService.getTableSequenceAndHeaders(
          user,
          processData.data[0]?.id
        )

        setTableSequenceAndHeaders(tableSequenceAndHeaders)

        const documents = await DocumentService.checkForTemplateDoc(user, activityTemplateId)
        const dynamicListData = await userConfig.getDynamicList(user)

        const listDataDep = (await dependencyMapService.getListDataDep(user, dataEntryActivityId))
          .data

        /* set all process data to context */
        setProcessContext({
          processData: processData.data[0],
          activityTemplate: allActivityData.activityTemplate,
          fieldConstraints: allActivityData.fieldConstraints,
          conditionalEnableDisable: conditionalEnableDisable,
          listDataMetadata: listDataMetadata,
          fieldTemplateDep: fieldTemplateDep,
          listDataDep: listDataDep,
          userTeamMap: userTeamMap.data,
          hiddenFields: hiddenFields,
          allFields: allSubfieldsResult.data,
          tableSubfieldsMap: tableSubfieldsMap.data,
          tableSubfields: tableSubfields.data,
          processAllRoutes: processAllRoutes.data,
          companyDetails: companyDetails,
          nonDefaultCurrency: nonDefaultCurrency,
          hasDocuments: documents && documents.length > 0,
          dynamicListData: dynamicListData.data,
        })

        const pageWiseFields = allSubfieldsResult.data?.reduce((acc, cur) => {
          if (acc[getPageId(cur.page_template_id)]) {
            return {
              ...acc,
              [getPageId(cur.page_template_id)]: [...acc[getPageId(cur.page_template_id)], cur],
            }
          } else {
            return { ...acc, [getPageId(cur.page_template_id)]: [cur] }
          }
        }, {})
        setPageDetails({ ...pageWiseFields })

        const initialAllValues = processData?.data[0]?.data || {}

        if (allActivityData?.activityTemplate?.activity_type != ACTIVITY_TYPES.REVIEW) {
          const { updatedFieldCurrencyMap, nonDefaultValues } = computeCurrency(
            fieldCurrencyMapData,
            allSubfieldsResult.data,
            companyDetails?.default_currency_code,
            allValues
          )

          let updatedCurrencyMap = {
            ...updatedFieldCurrencyMap,
            ...nonDefaultValues.reduce((acc, cur) => {
              return { ...acc, [cur.id]: cur.currency }
            }, {}),
          }

          // need to update the sum across process field currency to db
          for (let [key, value] of Object.entries(ndc)) {
            if (value !== companyDetails?.default_currency_code) {
              await currencyFieldService.addNonDefaultCurrency(user, {
                field_template_id: key,
                currency_code: value,
                process_id: processContext?.processData?.id,
              })
            }
          }

          setInitialFieldCurrencyMap({
            ...fieldCurrencyMapData,
            ...updatedCurrencyMap,
            ...Object.entries(ndc).reduce((acc, [key, value]) => {
              return { ...acc, [key]: CURRENCY_COUNTRY_CODE[value] }
            }, {}),
          })
        } else {
          setInitialFieldCurrencyMap(fieldCurrencyMapData)
        }

        /* set all values to allValues store */
        setInitialAllValues({ ...initialAllValues, ...computedSumAcrossProcess })

        if (
          allActivityData?.activityTemplate?.activity_type == ACTIVITY_TYPES.DATA_ENTRY &&
          processData?.data[0]?.version > 1
        ) {
          setReviewValues(previousProcessData?.data?.prevReviewProcessData?.review_data || {})
          setCommentValues(
            previousProcessData?.data?.prevReviewProcessData?.review_comments_data || {}
          )
        } else {
          setReviewValues(processData?.data[0]?.review_data)
          setCommentValues(processData?.data[0]?.review_comments_data)
        }
        setSelectedData(processData?.data[0]?.selectable_tables_data)

        setIsPageReady(true)
        setContentLoaded(true)
      } catch (e) {
        console.error('loading all form data', e)
      }
    })()
  }, [user])

  useEffect(() => {
    ;(async () => {
      if (!currentPageIndex && pages.length > 0 && processData?.data[0]?.id) {
        const defaultNavigatePage = await pageTemplateService.getDefaultPage(
          user,
          processData?.data[0]?.id
        )
        const pageIndex = pages.findIndex(
          (page) => page.sequence == defaultNavigatePage?.default_page_sequence
        )

        setCurrentPageIndex(() => pageIndex)
      }
    })()
  }, [pages])

  const handleClickOnGo = async (status) => {
    // Find missing fields
    const idFieldMap = processContext.allFields?.reduce((acc, cur) => {
      return { ...acc, [cur.id]: cur }
    }, {})

    // check if there any validation constraints present in store
    let fieldValidationErrorUpdated = {}
    let otherConstraintErrors = []

    /* prevent process creation validations */
    const processCreationStatus = await processDataService.checkProcessCreationStatus(
      user,
      processContext?.processData?.process_template_id,
      processContext?.processData?.activity_template_id,
      processContext?.processData?.id
    )

    if (processCreationStatus?.data?.messages?.length > 0) {
      processCreationStatus?.data?.messages.forEach((message) => {
        otherConstraintErrors.push(message)
      })
    }

    const routeType = allToRoutes.filter(
      (toRoute) => Number(toRoute.id) === Number(selectedToRoute)
    )[0].route_type
    /* ============= Required Field Validation ============== */

    // unique ids to prevent validation
    let requiredFieldTemplates = allSubfields.filter(
      (ft) => ft.is_required && ft.data_structure !== FIELD_TYPES.TABLE
    )

    if (activityTemplate?.activity_type == ACTIVITY_TYPES.DATA_ENTRY && processData?.version >= 1) {
      if (routeType !== ROUTE_TYPES.REGRESS) {
        // checking based on all required fields
        const requiredValidations = requiredFieldValidation(
          user,
          allSubfields,
          requiredFieldTemplates,
          processContext.conditionalEnableDisable,
          allValues,
          selectableTableData
        )

        /* ============= FC & TFC Validation ============== */
        //check for constraint validation
        const constraintsValidations = await finalConstraintsValidation(
          tableConstraints,
          fieldConstraints,
          allSubfields,
          listDataMetadata,
          allValues,
          selectableTableData,
          user,
          processId
        )

        const allConstraints = [...requiredValidations, ...constraintsValidations]

        allConstraints.forEach((it) => {
          fieldValidationErrorUpdated = { ...fieldValidationErrorUpdated, ...it }
        })
      }
    }

    /* ============= Review Related Validation ============== */

    // checks for review activity
    // for all rejected value there must be comments
    // to move forward -> all values must be accepted
    if (activityTemplate?.activity_type === REVIEW_CONTEXTS.REVIEW) {
      const rejectedKeys = Object.entries(reviewValues)
        .filter(([key, value]) => !key.includes('tf_') && key !== 'updatedAt' && !value)
        .map(([key, _]) => key)

      const isAllTrue = rejectedKeys.length <= 0
      if (routeType === ROUTE_TYPES.PROGRESS) {
        if (!isAllTrue) {
          const fieldWhichMissingComment = rejectedKeys.map(
            (key) => idFieldMap[getFieldIdFromKey(key)?.id]
          )
          const msgExtension = fieldWhichMissingComment
            .map((it, index) => {
              if (it.is_subfield && it.table_field_template_id) {
                return `${index + 1}. ${
                  idFieldMap[it.table_field_template_id].field_template_name
                }/${it?.field_template_name}`
              } else {
                return `${index + 1}. ${it?.field_template_name}`
              }
            })
            .join('\n')

          otherConstraintErrors.push(
            <span style={{ fontSize: 12 }}>
              All data entered must be accepted to send forward.
              <br />
              <br />
              Rejected fields:
              <br />
              {msgExtension.split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  <br />
                </span>
              ))}
            </span>
          )
        }
      } else if (routeType === ROUTE_TYPES.REGRESS) {
        if (
          isAllTrue &&
          showGoBackWarningDialog === false &&
          Object.keys(reviewValues).length > 0
        ) {
          setShowGoBackWarningDialog(true)
          return
        }

        let keysMissingComments = []
        let isAllCommentsAddedForRejects = true
        Object.keys(reviewValues)
          .filter((key) => !key.includes('tf_'))
          .forEach((key) => {
            if (
              reviewValues[key] === false &&
              commentValues[key?.replace('r_', 'c_')]?.trim() === ''
            ) {
              keysMissingComments.push(key)
              isAllCommentsAddedForRejects = false
            }
          })

        const fieldWhichMissingComment = keysMissingComments.map(
          (key) => idFieldMap[getFieldIdFromKey(key)?.id]
        )
        const msgExtension = fieldWhichMissingComment
          .map((it, index) => {
            if (it.is_subfield && it.table_field_template_id) {
              return `${index + 1}. ${idFieldMap[it.table_field_template_id].field_template_name}/${
                it?.field_template_name
              }`
            } else {
              return `${index + 1}. ${it?.field_template_name}`
            }
          })
          .join(',')

        if (!isAllCommentsAddedForRejects) {
          otherConstraintErrors.push(
            <span style={{ fontSize: 12 }}>
              All data rejected must have associated review comments.
              <br />
              <br />
              Fields missing comments:
              <br />
              {msgExtension.split(',').map((line, i) => (
                <span key={i}>
                  {line.trim()}
                  <br />
                </span>
              ))}
            </span>
          )
        }
      }
      // else if (routeType === ROUTE_TYPES.NEUTRAL) {
      //   let isAllCommentsAddedForRejects = true
      //   Object.keys(reviewValues)
      //     .filter((key) => !key.includes('tf_'))
      //     .map((key) => {
      //       if (
      //         reviewValues[key] == false &&
      //         commentValues[key?.replace('r_', 'c_')].trim() == ''
      //       ) {
      //         isAllCommentsAddedForRejects = false
      //       }
      //     })
      //   if (!isAllCommentsAddedForRejects) {
      //     otherConstraintErrors.push('All data rejected must have associated review comments.')
      //   }
      // }
    }

    /* ============= Query Related Validation ============== */

    //check for no open queries
    const queryData = await queryService.getQuery(user, processId)
    const hasOpenQueries = queryData.queries.some((q) => q.is_open == true)
    if (hasOpenQueries) {
      otherConstraintErrors.push(
        'Cannot submit as there are open/unanswered queries. You can either wait for the query to be answered or retract the query.'
      )
    }

    // check for the currency error
    // Important: Let user complete the process in this scenario
    // until we have ways to handle the computation across currency.

    /* for (const [key, value] of Object.entries(fieldCurrencyMap)) {
      if (value === '?') {
        const field = allSubfields.find((subfield) => subfield.id.toString() === key)
        if (field && allValues['f_' + field.id] && allValues['f_' + field.id] != 0) {
          otherConstraintErrors.push(`${field.field_template_name}'s currency is invalid!`)
        }
      }
    } */

    // check if there is any validation issues in otherConstraintErrors and fieldValidationErrorUpdated
    let skip = false
    setFieldValidationError(fieldValidationErrorUpdated)

    if (Object.values(fieldValidationErrorUpdated).filter((it) => it != null).length < 3) {
      Object.values(fieldValidationErrorUpdated).forEach((it) => {
        if (it) {
          skip = skip === false ? true : skip
          if (typeof it === 'string') {
            toast.error(it, TOAST_SETTINGS_ERROR)
          } else {
            toast(it, { ...TOAST_SETTINGS_ERROR, type: 'error' }) // JSX block
          }
        }
      })
    } else {
      skip = true
      toast.error(
        'Multiple mandatory fields lack entries. Fill in all highlighted fields to complete your submission.',
        TOAST_SETTINGS_ERROR
      )
      console.info('Field Constraints Validation Error: ', fieldValidationErrorUpdated)
    }

    otherConstraintErrors.forEach((it) => {
      toast.error(it, TOAST_SETTINGS_ERROR)
    })

    if (skip || otherConstraintErrors.length > 0) {
      setIsLoading(false)
      return
    }

    // at the end show preview and overall comments
    setSubmissionDialogOpen(status)
  }

  /* Task Submission */
  const handleSubmit = async (comment, routes) => {
    setIsLoading(true)

    //condition: overall must not be empty
    if (!comment || comment.trim() == '') {
      setIsLoading(false)
      toast.error('Overall comments must be entered.', TOAST_SETTINGS_ERROR)
      return
    }

    // finally submit for next activity
    const result = await processDataService.createNextProcess(user, processId, selectedToRoute, {
      overall_comment: comment.trim(),
      parallel_route_ids: routes.map((it) => it.id),
    })

    /* ==== Execute Outbound Actions ==== */
    const reservedKeywords = {
      process_reference_num: processData.process_reference_num,
      prefix: processTemplate.prefix,
      process_template_id: processTemplate.id,
      activity_template_id: activityTemplate.id,
      user_id: user.id,
      next_route_template_id: selectedToRoute,
      overall_comment: comment.trim(),
      version: processData?.version || 1,
      next_process_id: result?.data?.id_next[0] || null,
      protected_list_data_id: result?.data?.protected_list_data_id || null,
      user_token: user.token,
    }

    await executeOutboundServerAction(
      user,
      processId,
      activityTemplateId,
      selectedToRoute,
      allValues,
      allSubfields,
      listDataMetadata,
      processContext?.tableSubfieldsMap || [],
      tableSequenceAndHeaders || [],
      reservedKeywords,
      fieldCurrencyMap,
      processContext?.companyDetails?.default_currency_code
    )

    // handle api response
    if ([201, 200].includes(result?.status)) {
      toast.success('Saved successfully.', TOAST_SETTINGS)
    } else {
      setIsLoading(false)
      toast.error('Error saving data.', TOAST_SETTINGS_ERROR)
    }

    setTimeout(() => navigate('/'), REDIRECT_TIMER)
  }

  // Auto scroll to top while changing page
  const autoScrollRef = useRef(null)
  useEffect(() => {
    if (currentPageIndex + 1 && autoScrollRef.current) {
      autoScrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [currentPageIndex])

  return (
    <div
      className="dataentry-card"
      style={{ height: '85vh', overflowY: 'scroll', padding: '0px 15px 0px 0px' }}
    >
      {processData && processTemplate && (
        <>
          <Typography
            className="fontWeight800 textprimaryblue"
            variant="h4"
            ref={autoScrollRef}
            style={{
              ...headingStyles,
              marginTop: 0,
              marginBottom: 0,
              fontSize: dataEntrysubtitle,
            }}
          >
            {processTemplate?.prefix}-{processData?.process_reference_num}
          </Typography>

          <Typography
            className="textPrimary fontWeight700"
            style={{
              ...paragraphStyles,
              marginBottom: 10,
              marginTop: 0,
              fontSize: dataEntryPageTitle,
            }}
          >
            {activityTemplate?.activity_template_name} (version: {processData?.version})
          </Typography>
        </>
      )}
      {isPageReady && currentPageIndex != null && contentLoaded ? (
        <>
          <div
            className="data-entntry-top-icon"
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              backgroundColor: '#efeaff',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '10px',
              boxShadow: '1px 2px 6px 0px rgb(57 85 120 / 11%)',
              marginBottom: '20px',
              border: '1px solid #ddd',
              padding: '10px 15px',
              borderRadius: '3px',
            }}
          >
            <DataEntryPageNav
              pages={pages}
              processId={processId}
              setCurrentPageIndex={setCurrentPageIndex}
              currentPageIndex={currentPageIndex}
              setOverallCommentsOpen={handleClickOnGo}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              processReferenceNum={processData?.process_reference_num}
              processTemplateId={processTemplate?.id}
              reviewContext={activityTemplate?.activity_type}
              onSubmit={handleSubmit}
              isPageReady={isPageReady}
              processTemplate={processTemplate}
            />
          </div>

          <div className="forminput">
            <Page page={pages[currentPageIndex]} />
          </div>

          {/* <hr style={{ marginBottom: 16, marginTop: 0 }}></hr> */}
        </>
      ) : (
        <div style={{ marginTop: 14 }}>
          <Loader message="Setting up pages for you..." />
        </div>
      )}

      {ACTIVITY_TYPES.LONG_RUNNING === activityTemplate?.activity_type &&
      !user.teams.some((team) => activityTemplate.lrp_reviewer_team_ids.includes(team)) ? null : (
        <DataEntryFormFooter
          currentPageNum={currentPageIndex + 1}
          totalPage={pages.length}
          processActivityStatus={processData?.status}
          setSubmissionDialogOpen={handleClickOnGo}
          toRoutes={allToRoutes}
          selectedToRoute={selectedToRoute}
          setSelectedToRoute={setSelectedToRoute}
          classes={classes}
          processReferenceNum={processData?.id}
          allValues={allValues}
          fieldConstraintHiddenRoutes={fieldConstraintHiddenRoutes}
        />
      )}

      {isSubmissionDialogOpen && (
        <DataPreview
          processId={processId}
          onClose={() => setSubmissionDialogOpen(false)}
          open={!!processId}
          onSubmit={handleSubmit}
          routeTemplateId={selectedToRoute}
          isSubmission={isSubmissionDialogOpen}
        />
      )}
      {showGoBackWarningDialog && (
        <ConfirmationBox
          title={confirmationText}
          isDialogOpen={showGoBackWarningDialog}
          setIsDialogOpen={setShowGoBackWarningDialog}
          onYes={handleClickOnGo}
        />
      )}
      <Dialog open={isLoading} width="lg">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Typography variant="h6" color="textSecondary" style={{ marginBottom: '10px' }}>
            Saving your response...
          </Typography>
          <CircularProgress color="primary" size={30} />
        </div>
      </Dialog>
    </div>
  )
}

export default DataEntryPage
